/**
 * Request lifecycle helpers for the dashboard module.
 *
 * The dashboard code carried five near-identical copies of the "was this
 * cancelled?" predicate (viewer, list, preview, workbench, inventory config).
 * They drifted — some checked `axios.isCancel`, some only two of the three
 * error shapes — so whether navigating away produced an error tile depended on
 * which file happened to own the request. One definition now.
 *
 * The distinction this file exists to protect:
 *
 *   Widget is loading -> user navigates away -> request aborted
 *     => EXPECTED lifecycle event. No error state, no notification.
 *
 *   Widget is mounted -> request fails
 *     => GENUINE failure. Show it.
 *
 * Cancellation is not a failure mode, and suppressing *all* widget errors to
 * hide it would hide the real ones too.
 */

/**
 * True when an error represents an intentionally cancelled/aborted request.
 *
 * Covers every shape the stack can produce: axios' own marker, axios v1's
 * `ERR_CANCELED` / `CanceledError`, the native `AbortError` a fetch/AbortSignal
 * raises, and the bare `'canceled'` message older axios versions threw.
 */
export const isCancellation = (err) => {
  if (!err) return false;
  return (
    err.__CANCEL__ === true ||
    err.code === 'ERR_CANCELED' ||
    err.name === 'CanceledError' ||
    err.name === 'AbortError' ||
    err.message === 'canceled'
  );
};

/** Error taxonomy (requirement §10). */
export const ERROR_KIND = {
  CANCELLED: 'REQUEST_CANCELLED',
  FAILED: 'REQUEST_FAILED',
  TIMEOUT: 'REQUEST_TIMEOUT',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  SERVER_ERROR: 'SERVER_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  NO_DATA: 'NO_DATA',
};

/**
 * Classify an error so callers can decide how to surface it.
 *
 * `CANCELLED` must never reach a widget's error state — callers should return
 * early on it. Everything else is a real condition worth showing.
 */
export const classifyError = (err) => {
  if (isCancellation(err)) return ERROR_KIND.CANCELLED;
  if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
    return ERROR_KIND.TIMEOUT;
  }
  const status = err?.response?.status;
  if (status === 401) return ERROR_KIND.UNAUTHORIZED;
  if (status === 403) return ERROR_KIND.FORBIDDEN;
  if (typeof status === 'number' && status >= 500) return ERROR_KIND.SERVER_ERROR;
  // An axios error with a request but no response never reached the server.
  if (!err?.response && err?.request) return ERROR_KIND.NETWORK_ERROR;
  return ERROR_KIND.FAILED;
};

/** Human-readable, actionable message for a classified error. */
export const describeError = (err) => {
  switch (classifyError(err)) {
    case ERROR_KIND.CANCELLED:
      return null; // never rendered
    case ERROR_KIND.TIMEOUT:
      return 'Query timed out. Try a shorter time range or a simpler query.';
    case ERROR_KIND.UNAUTHORIZED:
      return 'Your session expired. Sign in again to load this widget.';
    case ERROR_KIND.FORBIDDEN:
      return "You don't have permission to view this data.";
    case ERROR_KIND.NETWORK_ERROR:
      return 'Network unreachable. Check your connection and retry.';
    case ERROR_KIND.SERVER_ERROR:
    case ERROR_KIND.FAILED:
    default: {
      const data = err?.response?.data;
      if (data?.message) return data.message;
      if (typeof data?.error === 'string') return data.error;
      if (data?.error) return JSON.stringify(data.error);
      return err?.message || 'Failed to fetch data';
    }
  }
};

export default isCancellation;
